import { EXTERNAL_SYSTEM_META } from './systems';
import type { BlockedWhy, ExternalWriteBlockedProblem, ExternalWritesState, SystemLease } from './wire';

type LiveLease = Extract<SystemLease, { mode: 'live' }>;

export type TimeFormatter = (iso: string) => string;

export const formatTime: TimeFormatter = (iso) =>
  new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));

export const WHY_WORDS: Record<BlockedWhy, string> = {
  'no-lease': 'no lease',
  expired: 'lease expired',
  revoked: 'lease revoked',
  ceiling: 'blocked by TERRA_WRITES_CEILING',
  'db-error': 'lease unreadable',
};

const label = (l: SystemLease): string => EXTERNAL_SYSTEM_META[l.system].label;

/** Live means a live lease that has not yet run out. The one predicate every description uses. */
const isLive = (l: SystemLease, now: Date): l is LiveLease => l.mode === 'live' && Date.parse(l.liveUntil) > now.getTime();

/** One popover row. `undefined` = the state is not loaded yet. */
export function describeRow(lease: SystemLease | undefined, now: Date, format: TimeFormatter = formatTime): string {
  if (!lease) return 'unknown';
  if (isLive(lease, now)) return `live until ${format(lease.liveUntil)} · ${lease.setBy.name}: ${lease.reason}`;
  if (lease.mode === 'sandbox') return `sandboxed (${WHY_WORDS[lease.why]})`;
  return 'sandboxed (lease expired)';
}

export function describeLeases(
  state: ExternalWritesState,
  now: Date,
  format: TimeFormatter = formatTime,
): { tone: 'none' | 'amber' | 'red'; headline: string; sandboxedLine: string | null; soonestExpiry: string | null } {
  const liveLeases = state.systems.filter((l): l is LiveLease => isLive(l, now));
  const others = state.systems.filter((l) => !isLive(l, now));

  if (liveLeases.length === 0) {
    return { tone: 'none', headline: 'SANDBOX: all external writes blocked', sandboxedLine: null, soonestExpiry: null };
  }

  const soonestExpiry = liveLeases.map((l) => l.liveUntil).reduce((a, b) => (Date.parse(b) < Date.parse(a) ? b : a));
  // Leases carry no set-at time, so the latest expiry is the proxy for the most recently set lease.
  const latest = liveLeases.reduce((a, b) => (Date.parse(b.liveUntil) > Date.parse(a.liveUntil) ? b : a));
  const who = `(${latest.setBy.name}: ${latest.reason})`;
  const until = format(soonestExpiry);

  if (others.length === 0) {
    return { tone: 'red', headline: `LIVE: all external systems until ${until} ${who}`, sandboxedLine: null, soonestExpiry };
  }

  const sandboxedNames = others.map((l) => label(l)).join(', ');
  const hasCeiling = others.some((l) => l.ceiling);
  return {
    tone: 'amber',
    headline: `LIVE: ${liveLeases.map(label).join(', ')} until ${until} ${who}`,
    sandboxedLine: `sandboxed: ${sandboxedNames}${hasCeiling ? ' (ceiling)' : ''}`,
    soonestExpiry,
  };
}

export function describeBlocked(p: ExternalWriteBlockedProblem, state: ExternalWritesState | null, now: Date): string {
  const base = `Sandbox: ${EXTERNAL_SYSTEM_META[p.system].label} write blocked (${WHY_WORDS[p.why]}).`;
  if (state === null) return base;
  const liveLabels = state.systems.filter((l) => isLive(l, now)).map(label);
  if (liveLabels.length === 0) return `${base} No system is live.`;
  if (liveLabels.length === 1) return `${base} ${liveLabels[0]} is the only live system.`;
  return `${base} Live: ${liveLabels.join(', ')}.`;
}
